<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('bmi.title') }}</h1>
      <p>{{ $t('bmi.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="form-section">
        <div class="form-row">
          <label>{{ $t('bmi.height') }}</label>
          <div class="input-group">
            <input type="number" v-model.number="height" placeholder="" min="50" max="250">
            <span class="unit">{{ $t('bmi.heightUnit') }}</span>
          </div>
          <div class="slider-row">
            <input type="range" v-model.number="height" min="100" max="220" class="slider">
          </div>
        </div>

        <div class="form-row">
          <label>{{ $t('bmi.weight') }}</label>
          <div class="input-group">
            <input type="number" v-model.number="weight" placeholder="" min="20" max="300">
            <span class="unit">{{ $t('bmi.weightUnit') }}</span>
          </div>
          <div class="slider-row">
            <input type="range" v-model.number="weight" min="30" max="150" class="slider">
          </div>
        </div>

        <button class="btn primary calculate-btn" @click="calculate">{{ $t('bmi.calculate') }}</button>
      </div>

      <div class="result-section" v-if="result">
        <div class="bmi-display" :class="result.category">
          <div class="bmi-value">{{ result.bmi }}</div>
          <div class="bmi-label">{{ $t('bmi.bmiIndex') }}</div>
          <div class="bmi-status">{{ result.status }}</div>
        </div>

        <div class="bmi-scale">
          <div class="scale-bar">
            <div class="scale-segment underweight">{{ $t('bmi.underweight') }}</div>
            <div class="scale-segment normal">{{ $t('bmi.normal') }}</div>
            <div class="scale-segment overweight">{{ $t('bmi.overweight') }}</div>
            <div class="scale-segment obese">{{ $t('bmi.obese') }}</div>
          </div>
          <div class="scale-pointer" :style="{ left: pointerPosition + '%' }">
            <div class="pointer-arrow">▼</div>
          </div>
          <div class="scale-numbers">
            <span>14</span>
            <span>18.5</span>
            <span>24</span>
            <span>28</span>
            <span>35</span>
          </div>
        </div>

        <div class="info-cards">
          <div class="info-card">
            <div class="info-icon">📊</div>
            <div class="info-content">
              <div class="info-title">{{ $t('bmi.healthyRange') }}</div>
              <div class="info-value">{{ result.healthyWeightMin }} - {{ result.healthyWeightMax }} kg</div>
            </div>
          </div>
          <div class="info-card">
            <div class="info-icon">🎯</div>
            <div class="info-content">
              <div class="info-title">{{ $t('bmi.idealWeight') }}</div>
              <div class="info-value">{{ result.idealWeight }} kg</div>
            </div>
          </div>
          <div class="info-card">
            <div class="info-icon">⚖️</div>
            <div class="info-content">
              <div class="info-title">{{ $t('bmi.suggestion') }}</div>
              <div class="info-value">{{ result.suggestion }}</div>
            </div>
          </div>
        </div>

        <div class="standard-table">
          <h4>{{ $t('bmi.standardTable') }}</h4>
          <table>
            <thead>
              <tr>
                <th>{{ $t('bmi.category') }}</th>
                <th>{{ $t('bmi.bmiRange') }}</th>
                <th>{{ $t('bmi.healthRisk') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr :class="{ active: result.category === 'underweight' }">
                <td>{{ $t('bmi.underweight') }}</td>
                <td>&lt; 18.5</td>
                <td>{{ $t('bmi.malnutritionRisk') }}</td>
              </tr>
              <tr :class="{ active: result.category === 'normal' }">
                <td>{{ $t('bmi.normal') }}</td>
                <td>18.5 - 23.9</td>
                <td>{{ $t('bmi.healthyWeight') }}</td>
              </tr>
              <tr :class="{ active: result.category === 'overweight' }">
                <td>{{ $t('bmi.overweight') }}</td>
                <td>24 - 27.9</td>
                <td>{{ $t('bmi.increased') }}</td>
              </tr>
              <tr :class="{ active: result.category === 'obese' }">
                <td>{{ $t('bmi.obese') }}</td>
                <td>≥ 28</td>
                <td>{{ $t('bmi.highRisk') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('bmi.whatIsBmi') }}</h2>
      <p>{{ $t('bmi.whatIsBmiDesc') }}</p>

      <h2>{{ $t('bmi.bmiLimitations') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('bmi.bmiLimitationsItems')" :key="'lim'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('bmi.bmiStandards') }}</h2>
      <p>{{ $t('bmi.bmiStandardsDesc') }}</p>

      <h2>{{ $t('bmi.otherIndicators') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('bmi.otherIndicatorsItems')" :key="'ind'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('bmi.healthyWeightTips') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('bmi.healthyWeightTipsItems')" :key="'hwt'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('bmi.idealWeightFormulas') }}</h2>
      <p>{{ $t('bmi.idealWeightFormulasDesc') }}</p>
      <ul>
        <li v-for="(item, index) in $t('bmi.idealWeightFormulasItems')" :key="'iwf'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('bmi.bmiAndHealth') }}</h2>
      <p>{{ $t('bmi.bmiAndHealthDesc') }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BMICalculator',
  data() {
    return {
      height: 170,
      weight: 65,
      result: null
    }
  },
  computed: {
    pointerPosition() {
      if (!this.result) return 50
      const bmi = parseFloat(this.result.bmi)
      // Map BMI 14-35 to 0-100%
      const position = ((bmi - 14) / (35 - 14)) * 100
      return Math.max(0, Math.min(100, position))
    }
  },
  methods: {
    calculate() {
      if (!this.height || !this.weight) return

      const heightM = this.height / 100
      const bmi = this.weight / (heightM * heightM)
      
      let category, status
      if (bmi < 18.5) {
        category = 'underweight'
        status = this.$t('bmi.underweight')
      } else if (bmi < 24) {
        category = 'normal'
        status = this.$t('bmi.normal')
      } else if (bmi < 28) {
        category = 'overweight'
        status = this.$t('bmi.overweight')
      } else {
        category = 'obese'
        status = this.$t('bmi.obese')
      }

      // 健康体重范围 (BMI 18.5-23.9)
      const healthyWeightMin = (18.5 * heightM * heightM).toFixed(1)
      const healthyWeightMax = (23.9 * heightM * heightM).toFixed(1)
      
      // 理想体重 (BMI 22)
      const idealWeight = (22 * heightM * heightM).toFixed(1)
      
      // 体重建议
      let suggestion
      const diff = this.weight - parseFloat(idealWeight)
      if (Math.abs(diff) < 2) {
        suggestion = this.$t('bmi.keepWeight')
      } else if (diff > 0) {
        suggestion = this.$t('bmi.loseWeight').replace('{n}', diff.toFixed(1))
      } else {
        suggestion = this.$t('bmi.gainWeight').replace('{n}', Math.abs(diff).toFixed(1))
      }

      this.result = {
        bmi: bmi.toFixed(1),
        category,
        status,
        healthyWeightMin,
        healthyWeightMax,
        idealWeight,
        suggestion
      }
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 700px;
  margin: 0 auto;
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  font-size: 16px;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
}

.form-section {
  margin-bottom: 32px;
}

.form-row {
  margin-bottom: 24px;
}

.form-row > label {
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.input-group {
  display: flex;
  align-items: center;
}

.input-group input[type="number"] {
  flex: 1;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--text-primary);
  font-size: 18px;
}

.input-group input:focus {
  outline: none;
  border-color: var(--accent-color);
}

.input-group .unit {
  margin-left: 12px;
  color: var(--text-secondary);
  font-size: 14px;
  white-space: nowrap;
}

.slider-row {
  margin-top: 12px;
}

.slider {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: var(--bg-tertiary);
  appearance: none;
  cursor: pointer;
}

.slider::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--accent-color);
  cursor: pointer;
}

.calculate-btn {
  width: 100%;
  padding: 14px;
  font-size: 16px;
}

.btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn.primary {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

.btn.primary:hover {
  background: var(--accent-hover);
}

.result-section {
  padding-top: 24px;
  border-top: 1px solid var(--border-color);
}

.bmi-display {
  text-align: center;
  padding: 32px;
  border-radius: 16px;
  margin-bottom: 24px;
}

.bmi-display.underweight {
  background: linear-gradient(135deg, #60a5fa20 0%, #3b82f620 100%);
  border: 1px solid #60a5fa50;
}

.bmi-display.normal {
  background: linear-gradient(135deg, #22c55e20 0%, #16a34a20 100%);
  border: 1px solid #22c55e50;
}

.bmi-display.overweight {
  background: linear-gradient(135deg, #f59e0b20 0%, #d9760020 100%);
  border: 1px solid #f59e0b50;
}

.bmi-display.obese {
  background: linear-gradient(135deg, #ef444420 0%, #dc262620 100%);
  border: 1px solid #ef444450;
}

.bmi-value {
  font-size: 64px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.bmi-label {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 8px 0;
}

.bmi-status {
  font-size: 24px;
  font-weight: 600;
}

.bmi-display.underweight .bmi-status { color: #3b82f6; }
.bmi-display.normal .bmi-status { color: #22c55e; }
.bmi-display.overweight .bmi-status { color: #f59e0b; }
.bmi-display.obese .bmi-status { color: #ef4444; }

.bmi-scale {
  position: relative;
  margin: 40px 0;
  padding: 0 10px;
}

.scale-bar {
  display: flex;
  height: 24px;
  border-radius: 12px;
  overflow: hidden;
}

.scale-segment {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: white;
  font-weight: 500;
}

.scale-segment.underweight { background: #3b82f6; }
.scale-segment.normal { background: #22c55e; }
.scale-segment.overweight { background: #f59e0b; }
.scale-segment.obese { background: #ef4444; }

.scale-pointer {
  position: absolute;
  top: -20px;
  transform: translateX(-50%);
  transition: left 0.3s;
}

.pointer-arrow {
  color: var(--text-primary);
  font-size: 16px;
}

.scale-numbers {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: var(--text-secondary);
}

.info-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 600px) {
  .info-cards {
    grid-template-columns: 1fr;
  }
}

.info-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--bg-tertiary);
  border-radius: 12px;
}

.info-icon {
  font-size: 28px;
}

.info-title {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 4px;
}

.info-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.standard-table h4 {
  font-size: 16px;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.standard-table table {
  width: 100%;
  border-collapse: collapse;
}

.standard-table th,
.standard-table td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
}

.standard-table th {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 14px;
}

.standard-table td {
  color: var(--text-primary);
  font-size: 14px;
}

.standard-table tr.active {
  background: var(--accent-light);
}

.standard-table tr.active td {
  color: var(--accent-color);
  font-weight: 500;
}

.tool-description {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  margin-top: 32px;
  box-shadow: var(--shadow-md);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 28px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 12px;
}

.tool-description ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.tool-description li::before {
  content: '•';
  color: var(--accent-color);
  position: absolute;
  left: 0;
}
</style>
